import type { MouseEvent } from "react";

export interface Crumb {
  label: string;
  href?: string;
}

/** Breadcrumb trail ("Public alerts › Waiting for approval"); the last item is the current page. */
export function Breadcrumb({ items, onNavigate }: { items: Crumb[]; onNavigate?: (href: string) => void }) {
  return (
    <nav aria-label="Breadcrumb" className="lcc-crumbs">
      <ol>
        {items.map((c, i) => {
          const last = i === items.length - 1;
          const click = (e: MouseEvent<HTMLAnchorElement>) => {
            if (!onNavigate || !c.href || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey) return;
            e.preventDefault();
            onNavigate(c.href);
          };
          return (
            <li key={`${i}-${c.label}`}>
              {last || !c.href ? (
                <span aria-current={last ? "page" : undefined}>{c.label}</span>
              ) : (
                <a href={c.href} onClick={click}>
                  {c.label}
                </a>
              )}
              {!last && <span aria-hidden="true">›</span>}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

/** Up to two initials from a display name. */
export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  const first = parts[0][0] ?? "";
  const last = parts.length > 1 ? (parts[parts.length - 1][0] ?? "") : "";
  return (first + last).toUpperCase();
}

/** Initials on a tile-grey circle; the name is the accessible text unless `decorative`. */
export function Avatar({ name, size = 34, decorative }: { name: string; size?: number; decorative?: boolean }) {
  return (
    <span
      className="lcc-avatar"
      style={{ width: size, height: size, fontSize: Math.round(size * 0.38) }}
      role={decorative ? undefined : "img"}
      aria-label={decorative ? undefined : name}
      aria-hidden={decorative || undefined}
    >
      {initials(name)}
    </span>
  );
}
